import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { CHAPTERS, PROPERTY } from "@/data/property";

export function ChapterExperience() {
  const ch = CHAPTERS[0];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="story" className="relative bg-background py-24 md:py-40 overflow-hidden">
      <span
        aria-hidden
        className="pointer-events-none absolute -top-6 left-4 md:left-12 font-display text-[9rem] md:text-[16rem] leading-none text-accent/10 select-none"
      >
        {ch.num}
      </span>

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
        <div className="lg:col-span-6 lg:col-start-1">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.22em] text-accent font-medium mb-6">
              Chapter {ch.num} — {ch.kicker}
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground text-balance">
              {ch.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base lg:text-lg leading-relaxed font-light text-muted-foreground max-w-lg">
              {ch.body}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex items-center gap-4">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" strokeWidth={1} />
                ))}
              </div>
              <p className="text-sm text-foreground">
                <span className="font-medium">{PROPERTY.rating}/10</span>
                <span className="text-muted-foreground"> · {PROPERTY.reviewCount} verified VRBO reviews</span>
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5 lg:col-start-8">
          <div className="relative overflow-hidden rounded-[1.5rem] aspect-[4/5] shadow-2xl">
            <motion.img
              src={ch.image.src}
              alt={ch.image.alt}
              style={{ y }}
              className="absolute inset-0 h-[116%] w-full object-cover will-change-transform"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
